<script lang="ts" setup>
import { computed } from "vue";

import ArticleContent from "@/components/article/ArticleContent.vue";
import ArticleContentCollapser from "@/components/article/ArticleContentCollapser.vue";
import ContentsWithLinks from "@/components/article/politiken/ContentsWithLinks.vue";
import type {
  PolitikenAdvancedEnglishDanishArticle,
  PolitikenAdvancedEnglishDanishMeaning,
  PolitikenReference
} from "@/types/article-formats/politiken";

const props = defineProps<{
  article: PolitikenAdvancedEnglishDanishArticle;
  isCollapsible: boolean;
}>();

type Flexion = {
  flexion: string;
  pronunciation: string;
};

const flexions = computed<Flexion[]>(() => [
  {
    flexion: props.article.word.flexion1,
    pronunciation: props.article.word.pronunciationFlexion1
  },
  {
    flexion: props.article.word.flexion2,
    pronunciation: props.article.word.pronunciationFlexion2
  },
  {
    flexion: props.article.word.flexion3,
    pronunciation: props.article.word.pronunciationFlexion3
  },
  {
    flexion: props.article.word.flexion4,
    pronunciation: props.article.word.pronunciationFlexion4
  }
]);

const lemmaFlexions = computed<string[]>(() =>
  [
    props.article.word.flexion1Lemma3,
    props.article.word.flexion2Lemma3,
    props.article.word.flexion3Lemma3,
    props.article.word.flexion4Lemma3
  ].filter((lemmaFlexion) => lemmaFlexion !== "")
);

const getEquivalents = (meaning: PolitikenAdvancedEnglishDanishMeaning): PolitikenReference[][] =>
  [
    meaning.equivalent1,
    meaning.equivalent2,
    meaning.equivalent3,
    meaning.equivalent4,
    meaning.equivalent5,
    meaning.equivalent6
  ].filter((equivalent): equivalent is PolitikenReference[] => equivalent.length > 0);
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <ArticleContent>
    <div class="article-content__header article-header">
      <div class="article-header__content">
        <h5 class="article-header__word">{{ props.article.word.lemma }}</h5>
        <sup v-if="props.article.word.hnr" class="pol-header-sup">{{ props.article.word.hnr }}</sup>

        <span
          v-if="props.article.word.glossary"
          class="article-header__element article-header__element--comment article-header__element--chained"
        >
          {{ props.article.word.glossary }}
        </span>

        <span
          v-if="props.article.word.koLemma2"
          class="article-header__element article-header__element--comment article-header__element--chained"
        >
          {{ props.article.word.koLemma2 }}
        </span>
        <h5
          v-if="props.article.word.lemma2"
          class="article-header__word article-header__element article-header__element--chained"
        >
          {{ props.article.word.lemma2 }}
        </h5>

        <span
          v-if="props.article.word.koLemma22"
          class="article-header__element article-header__element--comment article-header__element--chained"
        >
          {{ props.article.word.koLemma22 }}
        </span>
        <h5
          v-if="props.article.word.lemma22"
          class="article-header__word article-header__element article-header__element--chained"
        >
          {{ props.article.word.lemma22 }}
        </h5>

        <span
          v-if="props.article.word.wordClass || props.article.word.wordClass2"
          class="article-header__element article-header__element--light article-header__element--italic"
        >
          {{ props.article.word.wordClass }}{{ props.article.word.wordClass2 }}
        </span>

        <span
          v-if="props.article.word.pronunciation"
          class="article-header__element article-header__element--comment article-header__element--chained"
          v-html="props.article.word.pronunciation"
        ></span>

        <span v-for="(flexion, flexionIndex) in flexions" :key="flexionIndex">
          <span
            v-if="flexion.flexion"
            class="article-header__inflections article-header__inflections--prominent"
            :class="{ 'article-header__inflections--chained': flexionIndex > 0 }"
            v-html="flexion.flexion"
          ></span>

          <span
            v-if="flexion.pronunciation"
            class="article-header__inflections article-header__inflections--chained"
            v-html="flexion.pronunciation"
          ></span>
        </span>

        <span
          v-if="props.article.word.koLemma3"
          class="article-header__element article-header__element--comment"
          :class="{
            'article-header__element--chained': props.article.word.koLemma3.charAt(0) !== ';',
            'article-header__element--unchained': props.article.word.koLemma3.charAt(0) === ';'
          }"
        >
          {{ props.article.word.koLemma3 }}
        </span>
        <h5
          v-if="props.article.word.lemma3"
          class="article-header__word article-header__element article-header__element--chained"
          v-html="props.article.word.lemma3"
        ></h5>

        <span
          v-if="props.article.word.pronunciationLemma3"
          class="article-header__element article-header__element--comment article-header__element--chained"
          v-html="props.article.word.pronunciationLemma3"
        ></span>

        <span
          v-for="(lemmaFlexion, lemmaFlexionIndex) in lemmaFlexions"
          :key="lemmaFlexionIndex"
          class="article-header__inflections article-header__inflections--prominent"
          :class="{ 'article-header__inflections--chained': lemmaFlexionIndex > 0 }"
          v-html="lemmaFlexion"
        ></span>
      </div>
    </div>

    <ArticleContentCollapser :is-collapsible="props.isCollapsible">
      <div class="article-content__body-container article-content__body-container--reading-lines">
        <div class="indentation-container">
          <div v-if="props.article.word.lemmaRemark.length > 0" class="indentation-container">
            <div class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">Bemærk:</div>
              <div class="article-content__politiken-grid-content">
                <ContentsWithLinks
                  v-for="(lemmaRemark, index) in props.article.word.lemmaRemark"
                  :key="index"
                  :content="lemmaRemark"
                  link-class="article-content__link"
                />
              </div>
              <div class="article-content__spacer" />
            </div>
          </div>

          <div
            v-for="(meaning, meaningIndex) in props.article.meanings"
            :key="meaningIndex"
            class="indentation-container"
          >
            <span
              v-if="meaning.bnr > 0"
              class="article-list-bullet article-list-bullet--primary article-list-bullet--prominent"
            >
              {{ meaning.bnr }}
            </span>

            <div v-if="meaning.glossary" class="article-content__text article-content__text--muted">
              &lt;{{ meaning.glossary }}&gt;
            </div>

            <span
              v-if="meaning.phrase"
              class="article-content__text article-content__text--secondary article-content__text--prominent"
            >
              {{ meaning.phrase }}
            </span>

            <span v-if="getEquivalents(meaning).length > 0">
              =<span
                v-for="(equivalent, equivalentIndex) in getEquivalents(meaning)"
                :key="equivalentIndex"
                class="article-content__text article-content__text--primary article-content__text--prominent ml-1"
              >
                <ContentsWithLinks
                  v-for="(reference, referenceIndex) in equivalent"
                  :key="referenceIndex"
                  :content="reference"
                  link-class="article-content__link"
                />
              </span>

              <br />
            </span>

            <div v-if="meaning.definition.length > 0" class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">
                Definition:
              </div>
              <div class="article-content__politiken-grid-content">
                <ContentsWithLinks
                  v-for="(reference, referenceIndex) in meaning.definition"
                  :key="referenceIndex"
                  :content="reference"
                  link-class="article-content__link"
                />
              </div>
            </div>

            <div v-if="meaning.wordClass || meaning.gramma" class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">
                Grammatik:
              </div>
              <div class="article-content__politiken-grid-content">
                <span class="article-content__text article-content__text--light pr-2">
                  {{ meaning.wordClass }} <span v-html="meaning.gramma"></span>
                </span>
              </div>
            </div>

            <div v-if="meaning.examples.length > 0" class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">
                Eksempel:
              </div>
              <div class="article-content__politiken-grid-content">
                <div
                  v-for="(example, exampleIndex) in meaning.examples"
                  :key="exampleIndex"
                  class="article-content__text article-content__text--secondary pr-2"
                >
                  &middot;
                  <span
                    v-if="example.grammar"
                    class="article-content__text article-content__text--light pr-1"
                    v-html="example.grammar"
                  ></span>
                  <span v-if="example.example" v-html="example.example"></span>
                </div>
              </div>
            </div>

            <div v-if="meaning.nb" class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">NB:</div>
              <div class="article-content__politiken-grid-content">
                <span class="article-content__text pr-2" v-html="meaning.nb"></span>
              </div>
            </div>

            <div v-if="meaning.usage" class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">Brug:</div>
              <div class="article-content__politiken-grid-content">
                <span class="article-content__text pr-2">{{ meaning.usage }}</span>
              </div>
            </div>

            <div v-if="meaning.synonym" class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">
                Synonym:
              </div>
              <div class="article-content__politiken-grid-content">
                <span class="article-content__text article-content__text--primary pr-2">
                  {{ meaning.synonym }}
                </span>
              </div>
            </div>

            <div v-if="meaning.antonym" class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">
                Antonym:
              </div>
              <div class="article-content__politiken-grid-content">
                <span class="article-content__text article-content__text--primary pr-2">
                  {{ meaning.antonym }}
                </span>
              </div>
            </div>

            <div v-if="meaning.reference.length > 0" class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">
                Henvisning:
              </div>
              <div class="article-content__politiken-grid-content">
                <ContentsWithLinks
                  v-for="(reference, referenceIndex) in meaning.reference"
                  :key="referenceIndex"
                  :content="reference"
                  link-class="article-content__link"
                />
              </div>
            </div>

            <div class="article-content__spacer" />
          </div>
        </div>
      </div>
    </ArticleContentCollapser>
  </ArticleContent>
</template>
